"use client";

import { AnimatePresence } from "framer-motion";
import { useCallback, useRef, useState, type ReactElement } from "react";
import { createPortal } from "react-dom";
import { CmarkdownvisualCanvas } from "./CmarkdownvisualCanvas";
import { CmarkdownvisualCompact } from "./CmarkdownvisualCompact";
import { CmarkdownvisualModal } from "./CmarkdownvisualModal";
import { CmarkdownvisualPortalContent } from "./CmarkdownvisualPortalContent";
import {
  CmarkdownvisualRenderSurface,
  getCmarkdownvisualRenderState,
} from "./CmarkdownvisualSelectors";
import { scrollNodeIntoCenter } from "./scrollNodeIntoCenter";
import type { CmarkdownvisualProps } from "./Cmarkdownvisual.types";
import { useCmarkdownvisualPortal } from "./useCmarkdownvisualPortal";

export {
  CMARKDOWNVISUAL_MODE,
  useCmarkdownvisualMode,
} from "./CmarkdownvisualModeContext";
export type { CmarkdownvisualMode } from "./CmarkdownvisualModeContext";
export type {
  CmarkdownvisualMetric,
  CmarkdownvisualProps,
} from "./Cmarkdownvisual.types";

export function Cmarkdownvisual({
  type,
  title,
  controls,
  modalControls,
  children,
  className,
  style,
  containerStyle,
  contentStyle,
  tabIndex,
  onKeyDown,
  compactPreview,
  metric,
  defaultExpanded,
  concaved = false,
}: CmarkdownvisualProps): ReactElement {
  const hasCompact = compactPreview != null;
  const [isExpanded, setIsExpanded] = useState(defaultExpanded ?? !hasCompact);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const expandedRef = useRef<HTMLDivElement | null>(null);
  const wasExpandedRef = useRef<boolean>(isExpanded);
  const portal = useCmarkdownvisualPortal({ isModalOpen });
  const renderState = getCmarkdownvisualRenderState({
    hasCompact,
    isExpanded,
    isModalOpen,
  });

  const closeModal = useCallback((): void => setIsModalOpen(false), []);
  const openModal = useCallback((): void => setIsModalOpen(true), []);
  const expandCanvas = useCallback((): void => setIsExpanded(true), []);
  const collapseCanvas = useCallback((): void => setIsExpanded(false), []);

  const handleExpandedAnimationComplete = useCallback((): void => {
    if (isExpanded && !wasExpandedRef.current) {
      wasExpandedRef.current = true;
      scrollNodeIntoCenter(expandedRef.current);
    }

    if (!isExpanded) {
      wasExpandedRef.current = false;
    }
  }, [isExpanded]);

  return (
    <>
      <AnimatePresence mode="wait" initial={false}>
        {renderState.surface === CmarkdownvisualRenderSurface.Compact ? (
          <CmarkdownvisualCompact
            key="compact"
            className={className}
            compactPreview={compactPreview}
            metric={metric}
            onExpand={expandCanvas}
            type={type}
          />
        ) : (
          <CmarkdownvisualCanvas
            key="expanded"
            canvasSlotRef={portal.setCanvasSlot}
            className={className}
            concaved={concaved}
            containerStyle={containerStyle}
            contentStyle={contentStyle}
            controls={controls}
            hasCompact={hasCompact}
            isModalOpen={isModalOpen}
            onAnimationComplete={handleExpandedAnimationComplete}
            onCollapse={collapseCanvas}
            onOpenModal={openModal}
            rootRef={expandedRef}
            style={style}
            title={title}
            type={type}
          />
        )}
      </AnimatePresence>
      <CmarkdownvisualModal
        bodyRef={portal.setModalSlot}
        isOpen={isModalOpen}
        modalControls={modalControls}
        onClose={closeModal}
        type={type}
      />
      {portal.portalHost
        ? createPortal(
            <CmarkdownvisualPortalContent
              keyboardWrapperRef={portal.keyboardWrapperRef}
              mode={renderState.mode}
              onKeyDown={onKeyDown}
              tabIndex={tabIndex}
            >
              {children}
            </CmarkdownvisualPortalContent>,
            portal.portalHost,
          )
        : null}
    </>
  );
}
